import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  BookMarked, ChevronDown, Download, Loader2, RefreshCw,
  CheckCircle2, AlertTriangle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/os-patch`;

/**
 * WindowsUpdateCatalogCard
 * ------------------------
 * `scott/kpi-cycle-update2` — the Windows Update catalog control moved here
 * from the OS Patch > Reports header.
 *
 * The catalog is what lets ALICE date a device's build to the Microsoft
 * release month it came from, which is the basis of every compliance figure
 * in the product. When it doesn't cover a build we cannot prove the device
 * is patched, so that device counts as non-compliant — the compliance
 * numbers read low until the catalog catches up.
 *
 * That is plumbing, and customers shouldn't have to reason about it while
 * reading a compliance report: they should be able to trust that the app
 * knows when each Windows build shipped. So the freshness detail, the
 * unresolved-build count and the manual sync all live on the admin Data
 * Management surface instead, next to the sync history they belong with.
 *
 * The nightly job (00:30 UTC, before the 01:00 compliance snapshot) keeps
 * this current on its own — the button is for when someone doesn't want to
 * wait.
 */
export default function WindowsUpdateCatalogCard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/catalog/stats`);
      setStats(res.data || null);
    } catch (e) {
      console.error('Failed to load Windows Update catalog stats', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/catalog/sync`);
      // The sync runs in the background; give it a moment before re-reading.
      setTimeout(async () => { await load(); setSyncing(false); }, 10000);
    } catch (e) {
      console.error('Catalog sync failed', e);
      setSyncing(false);
    }
  };

  const freshnessInfo = stats?.anchor_freshness || {};
  const {
    freshness, stale_anchors_count = 0, fresh_anchors_count = 0,
    hours_since_sync, stale_anchors = [], current_month,
  } = freshnessInfo;
  const coverage = stats?.device_build_coverage || null;
  const lastSync = stats?.last_sync;
  const totalEntries = stats?.total_entries;

  const tone = {
    fresh: { cls: 'text-emerald-600', dot: 'bg-emerald-500', Icon: CheckCircle2 },
    aging: { cls: 'text-amber-600', dot: 'bg-amber-500', Icon: AlertTriangle },
    stale: { cls: 'text-red-600', dot: 'bg-red-500', Icon: AlertTriangle },
  }[freshness] || { cls: 'text-muted-foreground', dot: 'bg-slate-400', Icon: AlertTriangle };

  let syncLabel;
  if (hours_since_sync == null) {
    syncLabel = stats?.sync_status === 'never_synced' ? 'Never synced' : 'Sync status unknown';
  } else if (hours_since_sync < 1) {
    syncLabel = 'Synced moments ago';
  } else if (hours_since_sync < 24) {
    syncLabel = `Synced ${Math.round(hours_since_sync)}h ago`;
  } else {
    syncLabel = `Synced ${Math.round(hours_since_sync / 24)}d ago`;
  }

  return (
    <Card data-testid="windows-update-catalog-card">
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="flex items-center gap-2 font-['Chivo']">
            <BookMarked className="h-5 w-5" />
            Windows Update Catalog
          </CardTitle>
          <CardDescription>
            Microsoft's build → release-month history, used to date every device's
            Windows build. Refreshed automatically each night at 00:30 UTC, before
            the daily compliance snapshot.
          </CardDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleSync}
          disabled={syncing || loading}
          data-testid="catalog-sync-now-btn"
          className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] font-semibold border-none shrink-0"
        >
          {syncing
            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Syncing…</>
            : <><Download className="h-4 w-4 mr-2" />Sync now</>}
        </Button>
      </CardHeader>

      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading catalog status…
          </div>
        ) : !stats ? (
          <div className="text-sm text-muted-foreground py-2">
            Catalog status unavailable.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <span className={`inline-flex items-center gap-2 font-medium ${tone.cls}`} data-testid="catalog-freshness-status">
                <span className={`inline-block w-2 h-2 rounded-full ${tone.dot}`} />
                {syncLabel}
              </span>
              <span className="text-muted-foreground">
                <span className="font-mono">{totalEntries ?? 0}</span> catalog entries
              </span>
              {stats?.scope === 'central' && (
                <span
                  className="text-muted-foreground"
                  title="One catalog shared by every organisation, kept current by ALICE"
                  data-testid="catalog-scope-central"
                >
                  Shared ALICE catalog
                </span>
              )}
              <span className="text-muted-foreground">
                Covers up to <span className="font-mono">{current_month || '—'}</span>
              </span>
            </div>

            {/* The number that explains a low compliance figure. Admin-only by
                design — see the component docstring. */}
            {coverage && coverage.total_devices > 0 && (
              <div
                className={`p-3 rounded-lg border text-sm ${
                  coverage.unidentified === 0
                    ? 'border-emerald-200 bg-emerald-50/60'
                    : 'border-amber-200 bg-amber-50/60'
                }`}
                data-testid="catalog-device-coverage"
              >
                {coverage.unidentified === 0 ? (
                  <span className="inline-flex items-center gap-2 text-emerald-800">
                    <CheckCircle2 className="h-4 w-4" />
                    All {coverage.total_devices} Windows devices are on a build we can date.
                  </span>
                ) : (
                  <>
                    <span className="inline-flex items-center gap-2 text-amber-800 font-medium">
                      <AlertTriangle className="h-4 w-4" />
                      {coverage.unidentified} of {coverage.total_devices} devices
                      ({coverage.unidentified_pct}%) are on a build the catalog doesn't cover
                    </span>
                    <p className="text-muted-foreground mt-1.5">
                      ALICE won't assume an unknown build is patched, so these devices count as
                      non-compliant and compliance reporting reads low until the catalog covers
                      them. A sync usually resolves it.
                    </p>
                    {coverage.sample_builds?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid="catalog-unresolved-builds">
                        {coverage.sample_builds.map((b) => (
                          <span
                            key={b.build}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/70 border text-xs font-mono"
                            title={`${b.devices} device${b.devices === 1 ? '' : 's'}`}
                          >
                            {b.build}
                            <span className="text-muted-foreground">×{b.devices}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div>
              <button
                type="button"
                onClick={() => setDetailsOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition"
                data-testid="catalog-details-toggle"
              >
                <ChevronDown className={`h-4 w-4 transition-transform ${detailsOpen ? '' : '-rotate-90'}`} />
                Catalog detail
                {stale_anchors_count > 0 && (
                  <span className="ml-1 px-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                    {stale_anchors_count}
                  </span>
                )}
              </button>

              {detailsOpen && (
                <div className="mt-3 space-y-3 text-sm" data-testid="catalog-details">
                  <div className="grid gap-1.5 sm:grid-cols-2">
                    <div>
                      <span className="text-muted-foreground">Last sync from Microsoft Graph: </span>
                      <span className="font-mono">
                        {lastSync ? new Date(lastSync).toLocaleString() : 'never'}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Built-in fallback anchors: </span>
                      <span className="font-mono">{fresh_anchors_count} current / {stale_anchors_count} behind</span>
                    </div>
                  </div>

                  {stale_anchors_count > 0 ? (
                    <div>
                      <div className="font-medium mb-1">
                        Windows versions whose built-in fallback is behind
                      </div>
                      <p className="text-muted-foreground mb-2">
                        These rely on the catalog to be dated correctly. Harmless while
                        the catalog is current.
                      </p>
                      <ul className="space-y-1">
                        {stale_anchors.map((s) => (
                          <li key={s.major_build} className="flex flex-wrap items-center gap-2"
                              data-testid={`stale-anchor-${s.major_build}`}>
                            <span className="font-mono text-muted-foreground">{s.major_build}</span>
                            <span>{s.os} {s.version}</span>
                            <span className="text-muted-foreground">
                              — newest built-in anchor {s.newest_anchor_month} ({s.months_behind}mo behind)
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="text-emerald-700">
                      ✓ Every in-service Windows version has a current fallback anchor for {current_month}.
                    </div>
                  )}

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={load}
                    className="text-muted-foreground"
                    data-testid="catalog-refresh-status-btn"
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                    Refresh status
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
